import { useEffect, useMemo, useRef, useState } from "react";
import BaseModal from "../modals/BaseModal";
import SearchInput from "../common/SearchInput";

const TYPE_LABELS = {
  call: "Calls",
  contact: "Contacts",
  lead: "Leads",
  appointment: "Appointments",
  campaign: "Campaigns",
};

function groupItems(items = []) {
  return items.reduce((groups, item) => {
    const key = item.type || "other";
    if (!groups[key]) {
      groups[key] = [];
    }
    groups[key].push(item);
    return groups;
  }, {});
}

export default function CommandPalette({
  open,
  query,
  items,
  loading,
  error,
  onQueryChange,
  onClose,
  onSelect,
}) {
  const inputRef = useRef(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const grouped = useMemo(() => groupItems(items), [items]);
  const flatItems = useMemo(() => items || [], [items]);

  useEffect(() => {
    if (!open) {
      return undefined;
    }

    const timer = window.setTimeout(() => {
      inputRef.current?.focus();
      inputRef.current?.select();
    }, 0);

    return () => window.clearTimeout(timer);
  }, [open]);

  useEffect(() => {
    setActiveIndex(0);
  }, [query, open]);

  const handleKeyDown = (event) => {
    if (event.key === "ArrowDown") {
      event.preventDefault();
      setActiveIndex((current) =>
        flatItems.length ? Math.min(current + 1, flatItems.length - 1) : 0,
      );
    }

    if (event.key === "ArrowUp") {
      event.preventDefault();
      setActiveIndex((current) => Math.max(current - 1, 0));
    }

    if (event.key === "Enter" && flatItems[activeIndex]) {
      event.preventDefault();
      onSelect(flatItems[activeIndex]);
    }
  };

  return (
    <BaseModal
      open={open}
      onClose={onClose}
      title="Search calls, contacts, and campaigns"
      meta="Find calls, contacts, leads, appointments, and campaigns."
      modalId="commandPalette"
      size="lg"
      className="command-palette-modal"
    >
      <SearchInput
        ref={inputRef}
        className="command-palette-search"
        value={query}
        onChange={(event) => onQueryChange(event.target.value)}
        onKeyDown={handleKeyDown}
        placeholder="Search by name, phone, status, campaign, or ID"
        showShortcut={false}
        aria-label="Workspace search query"
      />

      <div className="command-palette-results" role="listbox">
        {error ? (
          <div className="command-palette-state command-palette-state-error">
            {error}
          </div>
        ) : null}
        {!error && loading ? (
          <div className="command-palette-state">Searching...</div>
        ) : null}
        {!error && !loading && query.trim().length < 2 ? (
          <div className="command-palette-state">Type at least 2 characters.</div>
        ) : null}
        {!error && !loading && query.trim().length >= 2 && !flatItems.length ? (
          <div className="command-palette-state">No matching results.</div>
        ) : null}
        {!error && flatItems.length
          ? Object.entries(grouped).map(([type, group]) => (
              <div className="command-palette-group" key={type}>
                <div className="command-palette-group-title">
                  {TYPE_LABELS[type] || "Results"}
                </div>
                {group.map((item) => {
                  const itemIndex = flatItems.indexOf(item);
                  const active = itemIndex === activeIndex;

                  return (
                    <button
                      type="button"
                      key={item.id}
                      className={`command-palette-item${active ? " is-active" : ""}`}
                      onClick={() => onSelect(item)}
                      onMouseEnter={() => setActiveIndex(itemIndex)}
                      role="option"
                      aria-selected={active}
                    >
                      <span className="command-palette-item-main">
                        <span className="command-palette-item-title">{item.title}</span>
                        {item.subtitle ? (
                          <span className="command-palette-item-subtitle">
                            {item.subtitle}
                          </span>
                        ) : null}
                      </span>
                      <span className="command-palette-item-meta">
                        {item.matched_field || item.type}
                      </span>
                    </button>
                  );
                })}
              </div>
            ))
          : null}
      </div>
    </BaseModal>
  );
}
